import React from 'react';

import LoadingModal from 'src/components/navigation/LoadingModal';
import { getLocalUserId, useLogout } from 'src/features/auth';
import { useListUserMemberships } from 'src/features/profile/api/useListUserMemberships';
import { useParking } from 'src/providers/ParkingProvider';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { cn } from 'src/utils';

import Header from './Header';
import NavigationSidebar from './NavigationSidebar';
import {
    Dialog,
    DialogHead,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogSeparator,
    DialogHeader,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Button } from 'src/registry/ui/Button';
import { FormattedMessage } from 'react-intl';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { messages } from 'src/features/parking/utils/messages';
import { useParams } from 'react-router-dom';
import { Register } from 'src/features/auth/routes/Register';

const AppNavigationWrapper = ({ children }: { children: React.ReactNode }) => {
    const { sidePanelState, forceSidePanel, forceLeftStaticPanel } = useSidePanel();
    const userId = getLocalUserId();
    const [isSidePanelOpen, setIsSidePanelOpen] = React.useState(false);
    const { parkingName, parkingId, setParkingId, setParkingName } = useParking();
    const {
        data: userMemberships,
        isLoading: isLoadingUserMemberships,
        isError: isErrorMemberships,
    } = useListUserMemberships({ userId });

    const { mutate: logout } = useLogout({
        onSuccess: window.location.reload,
    });

    // If we don't have a initial parking id/name set the first occurance of the memberships list
    React.useEffect(() => {
        if (!userMemberships) return;
        if (!parkingName || !parkingId) {
            setParkingId(userMemberships[0]?.parking.id);
            setParkingName(userMemberships[0]?.parking.name);
        } else {
            const validMembership = userMemberships.find((m) => m.parking.id === parkingId);
            if (!validMembership) {
                setParkingId(userMemberships[0]?.parking.id);
                setParkingName(userMemberships[0]?.parking.name);
            }
        }
    }, [parkingId, parkingName, setParkingId, setParkingName, userMemberships]);

    React.useEffect(() => {
        const isOpen = Object.values(sidePanelState).some((value) => value);
        setIsSidePanelOpen(isOpen);
    }, [sidePanelState]);

    const { invitationParkingId, invitationId } = useParams();

    const onJoinParkingRoute = Boolean(invitationParkingId && invitationId);

    if (isLoadingUserMemberships) {
        return <LoadingModal isLoading={isLoadingUserMemberships} />;
    }

    if (onJoinParkingRoute) {
        return <Register />;
    }

    if (isErrorMemberships || userMemberships?.length == 0 || !userMemberships) {
        return (
            <Dialog open modal>
                <DialogContent className="" showOverlay={false} showCloseButton={false}>
                    <DialogHeader>
                        <DialogHead>
                            <DialogTitle>
                                <FormattedMessage {...messages.notAMember} />{' '}
                            </DialogTitle>
                        </DialogHead>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody className="flex-row gap-4">
                        <SquareIcon icon={faExclamationTriangle} />
                        <FormattedMessage {...messages.notAMemberDescription} />
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <Button variant="destructive" onClick={() => logout(undefined)}>
                            <FormattedMessage {...messages.logout} />
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }
    return (
        <>
            <NavigationSidebar />
            <div
                className={cn(
                    `transition-padding relative my-16 flex min-h-[calc(100vh-8rem)] w-full flex-col gap-6 rounded-3xl bg-white p-4 pt-8 shadow-md duration-300 ease-in-out sm:my-4 sm:ml-20 sm:min-h-[calc(100vh-2rem)] sm:max-w-[calc(100%-5rem)] sm:rounded-l-3xl sm:rounded-r-none sm:p-8 dark:bg-gray-900`,
                    isSidePanelOpen && 'sm:pr-104',
                    forceSidePanel && 'md:pr-104',
                    forceLeftStaticPanel && 'sm:pl-64',
                )}
            >
                {forceLeftStaticPanel && (
                    <div className="absolute inset-y-0 left-56 z-50 hidden w-px bg-gray-200 sm:block" />
                )}
                <Header userMemberships={userMemberships} />
                {children}
            </div>
        </>
    );
};

export default AppNavigationWrapper;
